/*
  LOCAL TESTING ONLY. Used instead of Auth0ProviderWithNavigate when
  REACT_APP_LOCAL_AUTH === 'true' (see src/index.tsx and .env.local-dev).

  Supplies its own Auth0Context value so useAuth0() / withAuthenticationRequired
  keep working everywhere. "Signing in" means choosing an email on /local-login;
  access tokens come from the local harness:
    GET ${REACT_APP_LOCAL_AUTH_URL}/local-auth/token?email=<email>
    -> { access_token, email, alias_mp }
  The token is an RS256 JWT the local API router's authorizer accepts.
*/
import React, {
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  Auth0Context,
  Auth0ContextInterface,
  LogoutOptions,
  RedirectLoginOptions,
  User
} from '@auth0/auth0-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'

import LocalLogin from './local-login'

export const LOCAL_LOGIN_PATH = '/local-login'
export const LOCAL_AUTH_STORAGE_KEY = 'akili.localAuth.email'

export const isLocalAuthEnabled = (): boolean =>
  process.env.REACT_APP_LOCAL_AUTH === 'true'

const getLocalAuthBaseUrl = (): string =>
  (process.env.REACT_APP_LOCAL_AUTH_URL || 'http://localhost:8080').replace(
    /\/+$/,
    ''
  )

export type LocalTokenResponse = {
  access_token: string
  email: string
  alias_mp: string
}

export const fetchLocalToken = async (
  email: string
): Promise<LocalTokenResponse> => {
  const url = `${getLocalAuthBaseUrl()}/local-auth/token?email=${encodeURIComponent(
    email
  )}`
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`Local auth token request failed (${res.status})`)
  }
  return res.json()
}

// Only same-app paths, so the local sign-in page can't bounce anywhere else.
export const sanitizeReturnTo = (returnTo?: string | null): string => {
  if (
    !returnTo ||
    !returnTo.startsWith('/') ||
    returnTo.startsWith('//') ||
    returnTo.startsWith(LOCAL_LOGIN_PATH)
  ) {
    return '/'
  }
  return returnTo
}

const readStoredEmail = (): string | null => {
  try {
    return window.sessionStorage.getItem(LOCAL_AUTH_STORAGE_KEY)
  } catch {
    return null
  }
}

const writeStoredEmail = (email: string | null) => {
  try {
    if (email) {
      window.sessionStorage.setItem(LOCAL_AUTH_STORAGE_KEY, email)
    } else {
      window.sessionStorage.removeItem(LOCAL_AUTH_STORAGE_KEY)
    }
  } catch {
    // sessionStorage unavailable: stay signed in for this page load only
  }
}

const loginRequiredError = () =>
  Object.assign(new Error('Login required'), {
    error: 'login_required',
    error_description: 'Login required'
  })

type CachedToken = { email: string; token: string; exp?: number }

const LocalAuthProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState<string | null>(readStoredEmail)
  const tokenCache = useRef<CachedToken | null>(null)

  const getTokenFor = useCallback(async (forEmail: string) => {
    const cached = tokenCache.current
    if (
      cached &&
      cached.email === forEmail &&
      (!cached.exp || cached.exp * 1000 > Date.now() + 30000)
    ) {
      return cached.token
    }

    const { access_token: token } = await fetchLocalToken(forEmail)
    let exp: number | undefined
    try {
      exp = jwtDecode(token).exp
    } catch {
      exp = undefined
    }
    tokenCache.current = { email: forEmail, token, exp }
    return token
  }, [])

  const getAccessTokenSilently = useCallback(
    async (options?: { detailedResponse?: boolean }) => {
      if (!email) {
        throw loginRequiredError()
      }
      const token = await getTokenFor(email)
      if (options?.detailedResponse) {
        const exp = tokenCache.current?.exp
        return {
          access_token: token,
          id_token: '',
          expires_in: exp
            ? Math.max(0, exp - Math.floor(Date.now() / 1000))
            : 3600
        }
      }
      return token
    },
    [email, getTokenFor]
  ) as unknown as Auth0ContextInterface['getAccessTokenSilently']

  const loginWithRedirect = useCallback(
    async (options?: RedirectLoginOptions) => {
      const params = (options?.authorizationParams || {}) as {
        screen_hint?: string
        initial_screen?: string
      }
      const returnTo = sanitizeReturnTo(
        options?.appState?.returnTo ||
          `${window.location.pathname}${window.location.search}`
      )
      const screenHint =
        params.screen_hint === 'signup' || params.initial_screen === 'signup'
          ? 'signup'
          : 'login'

      const query = new URLSearchParams({ returnTo, screen_hint: screenHint })
      navigate(`${LOCAL_LOGIN_PATH}?${query.toString()}`)
    },
    [navigate]
  )

  const logout = useCallback(
    async (options?: LogoutOptions) => {
      tokenCache.current = null
      writeStoredEmail(null)
      setEmail(null)

      const returnTo = options?.logoutParams?.returnTo
      if (!returnTo) {
        navigate('/')
        return
      }
      try {
        const url = new URL(returnTo, window.location.origin)
        if (url.origin === window.location.origin) {
          navigate(`${url.pathname}${url.search}`)
          return
        }
      } catch {
        // fall through
      }
      window.location.assign(returnTo)
    },
    [navigate]
  )

  // Called by the local sign-in page.
  const signIn = useCallback(
    async (newEmail: string, returnTo: string) => {
      const normalized = newEmail.trim().toLowerCase()
      await getTokenFor(normalized) // fail here if the harness isn't running
      writeStoredEmail(normalized)
      setEmail(normalized)
      navigate(sanitizeReturnTo(returnTo), { replace: true })
    },
    [getTokenFor, navigate]
  )

  const value = useMemo<Auth0ContextInterface<User>>(
    () => ({
      isAuthenticated: !!email,
      isLoading: false,
      error: undefined,
      user: email ? { email, name: email, email_verified: true } : undefined,
      getAccessTokenSilently,
      getAccessTokenWithPopup: async () =>
        (await getAccessTokenSilently()) as string,
      getIdTokenClaims: async () =>
        email ? { __raw: '', email, name: email } : undefined,
      loginWithRedirect,
      loginWithPopup: async () => loginWithRedirect(),
      logout,
      handleRedirectCallback: async () => ({ appState: undefined })
    }),
    [email, getAccessTokenSilently, loginWithRedirect, logout]
  )

  return (
    <Auth0Context.Provider value={value}>
      {location.pathname === LOCAL_LOGIN_PATH ? (
        <LocalLogin currentEmail={email} onSignIn={signIn} />
      ) : (
        children
      )}
    </Auth0Context.Provider>
  )
}

export default LocalAuthProvider
